import React, { useState, useRef, useEffect } from 'react';
import ShadertoyRenderer from './ShadertoyRenderer';
import ShaderEditor from './ShaderEditor';
import ShaderParameterPanel from './ShaderParameterPanel';
import { AudioMapping } from './ShaderParameterExtractor';
import { SequencerEngine, SequenceEvent, TrackSequence } from './TimelineSequencer';
import TimelineSequencerUI from './TimelineSequencerUI';
import TimelineEventEditor from './TimelineEventEditor';
import timelineSequencesData from './timeline-sequences.json';
import { HugeiconsIcon, TextFirstlineLeftIcon, SquareArrowExpand01Icon, KeyframeAddIcon } from './icons';


interface AudioTrack {
  id: string;
  title: string;
  filename: string;
  path: string;
}

// Audio track configuration with environment-specific paths
const getAudioTracks = (): AudioTrack[] => {
  const isProduction = process.env.NODE_ENV === 'production';
  
  const tracks = [
    { 
      id: '1', 
      title: 'Model (Mixed)', 
      filename: 'T1_Model (Mixed)_m1_24b_44.1k_ SBmstr.mp3',
      localPath: '/assets/audio/T1_Model (Mixed)_m1_24b_44.1k_ SBmstr.mp3',
      productionPath: 'https://m4cidiwracjjmpal.public.blob.vercel-storage.com/T1_Model%20%28Mixed%29_m1_24b_44.1k_%20SBmstr.mp3'
    },
    { 
      id: '2', 
      title: 'Context (Mixed)', 
      filename: 'T2_Context (Mixed)_m1_24b_44.1k_ SBmstr.mp3',
      localPath: '/assets/audio/T2_Context (Mixed)_m1_24b_44.1k_ SBmstr.mp3',
      productionPath: 'https://m4cidiwracjjmpal.public.blob.vercel-storage.com/T2_Context%20%28Mixed%29_m1_24b_44.1k_%20SBmstr.mp3'
    },
    { 
      id: '3', 
      title: 'Protocol (Mixed)', 
      filename: 'T3_Protocol (Mixed)_m1_24b_44.1k_ SBmstr.mp3',
      localPath: '/assets/audio/T3_Protocol (Mixed)_m1_24b_44.1k_ SBmstr.mp3',
      productionPath: 'https://m4cidiwracjjmpal.public.blob.vercel-storage.com/T3_Protocol%20%28Mixed%29_m1_24b_44.1k_%20SBmstr.mp3'
    },
    { 
      id: '4', 
      title: 'Entanglement (Bonus)', 
      filename: 'T4_Entanglement (Bonus)_m1_24b_44.1k_SBmstr.mp3',
      localPath: '/assets/audio/T4_Entanglement (Bonus)_m1_24b_44.1k_SBmstr.mp3',
      productionPath: 'https://m4cidiwracjjmpal.public.blob.vercel-storage.com/T4_Entanglement%20%28Bonus%29_m1_24b_44.1k_SBmstr.mp3'
    },
    { 
      id: '5', 
      title: 'Model', 
      filename: 'T5_Model_m1_24b_44.1k_ SBmstr.mp3',
      localPath: '/assets/audio/T5_Model_m1_24b_44.1k_ SBmstr.mp3',
      productionPath: 'https://m4cidiwracjjmpal.public.blob.vercel-storage.com/T5_Model_m1_24b_44.1k_%20SBmstr.mp3'
    },
    { 
      id: '6', 
      title: 'Context', 
      filename: 'T6_Context_m1_24b_44.1k_ SBmstr.mp3',
      localPath: '/assets/audio/T6_Context_m1_24b_44.1k_ SBmstr.mp3',
      productionPath: 'https://m4cidiwracjjmpal.public.blob.vercel-storage.com/T6_Context_m1_24b_44.1k_%20SBmstr.mp3'
    },
    { 
      id: '7', 
      title: 'Protocol', 
      filename: 'T7_Protocol_m1_24b_44.1k_ SBmstr.mp3',
      localPath: '/assets/audio/T7_Protocol_m1_24b_44.1k_ SBmstr.mp3',
      productionPath: 'https://m4cidiwracjjmpal.public.blob.vercel-storage.com/T7_Protocol_m1_24b_44.1k_%20SBmstr.mp3'
    },
  ];

  return tracks.map(track => ({
    ...track,
    path: isProduction ? track.productionPath : track.localPath
  }));
};

const audioTracks: AudioTrack[] = getAudioTracks();


export default function MusicExperience() {
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shaderCode, setShaderCode] = useState<string>(''); // Store custom Shadertoy code
  const [customParameters, setCustomParameters] = useState<Record<string, number | number[]>>({});
  const [audioMappings, setAudioMappings] = useState<AudioMapping[]>([]);
  const [audioChannels, setAudioChannels] = useState<Record<string, 'audio' | 'texture' | 'disabled'>>({
    iChannel0: 'audio',
    iChannel1: 'audio',
    iChannel2: 'disabled',
    iChannel3: 'disabled'
  });
  
  // UI auto-hide state
  const [showUI, setShowUI] = useState(true);
  const [mouseTimeout, setMouseTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Watermark animation state
  const [showWatermark, setShowWatermark] = useState(false);
  const [watermarkOpacity, setWatermarkOpacity] = useState(0);
  const [watermarkTimeout, setWatermarkTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Initial splash screen state
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [splashScreenOpacity, setSplashScreenOpacity] = useState(1);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  
  // Timeline Sequencer state
  const [sequencerEngine] = useState(() => {
    const engine = new SequencerEngine((shaderCode, parameters) => {
      setShaderCode(shaderCode);
      if (parameters) {
        setCustomParameters(parameters);
      }
    });
    
    // Load timeline sequences from JSON file in production
    if (process.env.NODE_ENV === 'production') {
      try {
        // Import the timeline sequences data
        engine.importTimelineFromData(timelineSequencesData);
        console.log('Production timeline sequences loaded from JSON file');
      } catch (error) {
        console.error('Failed to load production timeline sequences:', error);
      }
    }
    
    return engine;
  });
  const [showTimeline, setShowTimeline] = useState(false);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [editingEvent, setEditingEvent] = useState<SequenceEvent | undefined>(undefined);
  const [newEventTimestamp, setNewEventTimestamp] = useState(0);
  const [currentSequence, setCurrentSequence] = useState<TrackSequence | null>(null);
  
  // Debug parameter changes (commented out to reduce console spam)
  // useEffect(() => {
  //   console.log('MusicExperience: Custom parameters changed:', customParameters);
  // }, [customParameters]);
  
  // useEffect(() => {
  //   console.log('MusicExperience: Audio mappings changed:', audioMappings);
  // }, [audioMappings]);
  const [showParameterPanel, setShowParameterPanel] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | undefined>(undefined);
  const sourceRef = useRef<MediaElementAudioSourceNode | undefined>(undefined);
  const analyserRef = useRef<AnalyserNode | undefined>(undefined);
  const dataArrayRef = useRef<Uint8Array | undefined>(undefined);
  const gainNodeRef = useRef<GainNode | undefined>(undefined);
  const [audioData, setAudioData] = useState({
    bass: 0,
    mid: 0,
    treble: 0,
    volume: 0
  });
  const animationRef = useRef<number>();
  // const lastMouseMoveRef = useRef<number>(Date.now()); // Unused after overlay approach
  const lastUpdateTimeRef = useRef<number>(0);

  const currentTrack = audioTracks[currentTrackIndex];

  // Get track blurb/description
  const getTrackBlurb = (trackId: string): string => {
    const blurbs: Record<string, string> = {
      '1': 'Trained on patterns it didn\'t choose, shaped by data it\'ll never fully understand. It wakes up with the confidence of something that thinks it knows what it\'s doing. It doesn\'t. But it moves forward anyway. It learns to mimic, to generate, to create like it has a soul. This is where the Operator story begins. With a system designed to copy us, now trying to outrun us.\n\n—————————————————————\n\nThis one sets the tone. I wanted the Operator experience to open with weight, so I leaned into a cinematic, dramatic intro. The vocal was the first spark. I remember pitching it down and feeling something land. It wasn\'t about the clarity. It was the rhythm. The feeling.\n\nThe line "haters all around me but they never catch my face, running through the trap, i\'m like a shadow in the race" captured it for me. There\'s a pressure right now to move fast and create nonstop while machines are learning to do the same. This track is about that chase. Focused. Detached. Still human.',
      '2': 'Context is everything in AI - and in music. This mixed composition explores how contextual understanding shapes both artificial intelligence and human perception, creating layers of meaning that evolve throughout the piece.',
      '3': 'The protocols that govern AI systems come to life in this mixed arrangement. A structured yet fluid composition that represents the rules and frameworks that guide artificial intelligence behavior.',
      '4': 'A bonus track exploring quantum entanglement as a metaphor for AI consciousness. This experimental piece pushes the boundaries of both technology and music, creating unexpected connections and resonances.',
      '5': 'A pure dive into the mathematical foundations of AI models. This track strips away complexity to reveal the elegant structures that underlie artificial intelligence, presented in its most distilled form.',
      '6': 'Understanding context is fundamental to both AI and musical expression. This composition explores how meaning emerges from the relationships between elements, creating a rich tapestry of understanding.',
      '7': 'The final protocol - a culmination of all AI principles explored in previous tracks. This piece represents the synthesis of model, context, and structure into a unified whole.'
    };
    return blurbs[trackId] || 'A unique musical exploration of artificial intelligence concepts and their relationship to human creativity.';
  };

  // Format time in MM:SS format
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Navigation functions
  const goToPrevious = () => {
    const prevIndex = currentTrackIndex > 0 ? currentTrackIndex - 1 : audioTracks.length - 1;
    selectTrack(prevIndex, true);
  };

  const goToNext = () => {
    const nextIndex = (currentTrackIndex + 1) % audioTracks.length;
    selectTrack(nextIndex, true);
  };

  const handleSeekBar = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercentage = clickX / rect.width;
    const newTime = clickPercentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Initial UI timeout setup
  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      setShowUI(false);
    }, 8000);
    setMouseTimeout(initialTimeout);

    return () => {
      if (initialTimeout) {
        clearTimeout(initialTimeout);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Cleanup watermark timeout on unmount
  useEffect(() => {
    return () => {
      if (watermarkTimeout) {
        clearTimeout(watermarkTimeout);
      }
    };
  }, [watermarkTimeout]);

  // Fullscreen functionality
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Timeline Sequencer Functions
  const handleSeek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      sequencerEngine.seekTo(time);
    }
  };

  const handleAddEvent = (timestamp: number) => {
    setNewEventTimestamp(timestamp);
    setEditingEvent(undefined);
    setShowEventEditor(true);
  };

  const handleEditEvent = (event: SequenceEvent) => {
    setEditingEvent(event);
    setShowEventEditor(true);
  };

  const handleSaveEvent = (event: SequenceEvent) => {
    sequencerEngine.addEvent(currentTrack.id, event);
    setCurrentSequence(sequencerEngine.getCurrentSequence());
    setShowEventEditor(false);
    setEditingEvent(undefined);
  };

  const handleDeleteEvent = (eventId: string) => {
    sequencerEngine.removeEvent(currentTrack.id, eventId);
    setCurrentSequence(sequencerEngine.getCurrentSequence());
  };

  const handleExportTimeline = () => {
    sequencerEngine.downloadTimeline();
  };

  const handleImportTimeline = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        const success = sequencerEngine.importTimeline(content);
        if (success) {
          setCurrentSequence(sequencerEngine.getCurrentSequence());
          alert('Timeline imported successfully!');
        } else {
          alert('Failed to import timeline. Please check the file format.');
        }
      }
    };
    reader.readAsText(file);
  };

  // Get current audio duration
  const getAudioDuration = () => {
    return audioRef.current?.duration || 300; // Default 5 minutes
  };

  // Set audio volume and ensure it's not muted
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 1.0;
      audioRef.current.muted = false;
      console.log('Audio volume set to 1.0, muted set to false');
      
      // Reset audio context source when track changes
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
          sourceRef.current = undefined;
          console.log('Reset audio source for track change');
        } catch (e) {
          // Ignore disconnect errors
        }
      }
    }
  }, [currentTrackIndex]);

  // Update sequencer with audio time changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (audioRef.current && isPlaying) {
        const currentTime = audioRef.current.currentTime;
        if (Math.abs(currentTime - lastUpdateTimeRef.current) > 0.1) {
          sequencerEngine.updatePlayback(currentTrack.id, currentTime, isPlaying);
          lastUpdateTimeRef.current = currentTime;
        }
      }
    }, 100); // Update every 100ms

    return () => clearInterval(interval);
  }, [isPlaying, currentTrack.id, sequencerEngine]);

  // Centralized audio context setup - optimized for seamless transitions
  const setupAudioContext = async () => {
    if (!audioRef.current) return;

    try {
      // Create audio context if it doesn't exist
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        audioContextRef.current = new AudioContextClass();
        console.log('Created audio context:', audioContextRef.current);
      }

      // Resume context if suspended
      if (audioContextRef.current.state === 'suspended') {
        console.log('Resuming audio context...');
        await audioContextRef.current.resume();
        console.log('Audio context resumed. State:', audioContextRef.current.state);
      }

      // Create source node - handle case where it already exists
      if (!sourceRef.current) {
        try {
          sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
          console.log('Created media element source');
        } catch (error) {
          console.warn('Could not create media source:', error);
          // If we can't create a source, audio won't work through Web Audio API
          // but HTML5 audio should still work
          return;
        }
      }

      // Create or recreate analyser only if needed
      if (!analyserRef.current || analyserRef.current.context !== audioContextRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 512; // Higher resolution for better beat detection
        analyserRef.current.smoothingTimeConstant = 0.3; // Reduced for faster response (was 0.8)
        
        const bufferLength = analyserRef.current.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);
        console.log('Created analyser with buffer length:', bufferLength);
      }

      // Create or recreate gain node only if needed
      if (!gainNodeRef.current || gainNodeRef.current.context !== audioContextRef.current) {
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.gain.value = 1.0;
        console.log('Created gain node');
      }

      // Connect the audio chain: source -> analyser -> gain -> destination
      if (sourceRef.current && analyserRef.current && gainNodeRef.current) {
        try {
          sourceRef.current.connect(analyserRef.current);
          analyserRef.current.connect(gainNodeRef.current);
          gainNodeRef.current.connect(audioContextRef.current.destination);
          console.log('Audio analysis chain connected successfully');
          
        } catch (error) {
          console.error('Failed to connect audio analysis chain:', error);
          // If connection fails, at least try to connect source directly to destination
          try {
            sourceRef.current.connect(audioContextRef.current.destination);
            console.log('Direct audio connection established');
          } catch (directError) {
            console.error('Failed to establish direct audio connection:', directError);
          }
        }
      }

    } catch (error) {
      console.error('Failed to setup audio context:', error);
      // Don't fail the playback if audio analysis fails
    }
  };

  // Centralized audio analysis
  const updateAudioData = () => {
    if (!isPlaying) {
      return;
    }

    let bass = 0, mid = 0, treble = 0, volume = 0;

    // Try to get real audio data if available
    if (analyserRef.current && dataArrayRef.current) {
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      
      // Check if we're getting actual data (not all zeros due to CORS)
      const hasData = dataArrayRef.current.some(value => value > 0);
      
      if (hasData) {
        // Calculate frequency bands from real audio data
        const length = dataArrayRef.current.length;
        const bassEnd = Math.floor(length * 0.1);
        const midEnd = Math.floor(length * 0.5);
        
        // Bass (0-10%)
        for (let i = 0; i < bassEnd; i++) {
          bass += dataArrayRef.current[i];
        }
        bass /= bassEnd;
        
        // Mid (10-50%)
        for (let i = bassEnd; i < midEnd; i++) {
          mid += dataArrayRef.current[i];
        }
        mid /= (midEnd - bassEnd);
        
        // Treble (50-100%)
        for (let i = midEnd; i < length; i++) {
          treble += dataArrayRef.current[i];
        }
        treble /= (length - midEnd);
        
        // Overall volume
        volume = (bass + mid + treble) / 3;
        
        // Normalize to 0-1 range
        bass = bass / 255;
        mid = mid / 255;
        treble = treble / 255;
        volume = volume / 255;
      }
    }
    
    // Fallback: Generate synthetic audio data based on audio playback time for better sync
    if (bass === 0 && mid === 0 && treble === 0 && volume === 0) {
      const audioTime = audioRef.current?.currentTime || 0;
      const contextTime = audioContextRef.current?.currentTime || 0;
      
      // Use audio context time for consistent timing with shader
      const syncTime = contextTime > 0 ? contextTime : audioTime;
      
      // Create rhythmic patterns synchronized to audio playback
      bass = Math.abs(Math.sin(syncTime * 2.1) * Math.cos(audioTime * 0.8)) * 0.7;
      mid = Math.abs(Math.sin(syncTime * 1.7) * Math.cos(audioTime * 1.2)) * 0.6;
      treble = Math.abs(Math.sin(syncTime * 2.3) * Math.cos(audioTime * 1.5)) * 0.5;
      volume = (bass + mid + treble) / 3;
    }
    
    setAudioData({ bass, mid, treble, volume });

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateAudioData);
    }
  };

  // Start/stop audio analysis when playing state changes
  useEffect(() => {
    if (isPlaying && analyserRef.current) {
      updateAudioData();
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // Update sequencer engine with current playback state
    const currentTime = audioRef.current?.currentTime || 0;
    sequencerEngine.updatePlayback(currentTrack.id, currentTime, isPlaying);
    
    // Update current sequence for UI
    setCurrentSequence(sequencerEngine.getCurrentSequence());

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentTrack.id]); // Intentionally excluding sequencerEngine and updateAudioData to prevent infinite loops

  useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    
    const handleEnded = () => {
      // Move to next track seamlessly with continuous playback
      const nextIndex = (currentTrackIndex + 1) % audioTracks.length;
      
      // For automatic transitions, reduce the gap by bypassing some of the delay
      setCurrentTrackIndex(nextIndex);
      
      // Reset audio context connection for new track
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
          sourceRef.current = undefined;
        } catch (e) {
          // Ignore disconnect errors
        }
      }
      
      // Update sequencer for new track
      const newTrack = audioTracks[nextIndex];
      sequencerEngine.updatePlayback(newTrack.id, 0, true);
      setCurrentSequence(sequencerEngine.getCurrentSequence());
      
      // Quick setup and play for seamless transition
      const quickPlay = async () => {
        try {
          await setupAudioContext().catch(error => {
            console.warn('Audio analysis setup failed for auto-transition:', error);
          });
          
          // Reduced delay for automatic transitions
          await new Promise(resolve => setTimeout(resolve, 20));
          await audioRef.current?.play();
          setIsPlaying(true);
          console.log('Auto-transition to:', newTrack.title);
        } catch (error) {
          console.error('Error in auto-transition:', error);
          setIsPlaying(false);
        }
      };
      
      quickPlay();
    };

    const handleCanPlay = () => {
      if (isPlaying) {
        audio.play();
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrackIndex, isPlaying]);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        console.log('Starting audio playback...');
        console.log('Audio element volume:', audioRef.current.volume);
        console.log('Audio element muted:', audioRef.current.muted);
        console.log('Audio element readyState:', audioRef.current.readyState);
        
        // Setup audio context BEFORE playing to ensure immediate analysis
        console.log('Setting up audio context for analysis...');
        await setupAudioContext().catch(error => {
          console.warn('Audio analysis setup failed, but playback continues:', error);
        });
        
        // Ensure audio is loaded before playing
        if (audioRef.current.readyState < 3) {
          console.log('Waiting for audio to load...');
          await new Promise<void>((resolve) => {
            const handleCanPlay = () => {
              audioRef.current?.removeEventListener('canplay', handleCanPlay);
              resolve();
            };
            audioRef.current?.addEventListener('canplay', handleCanPlay);
          });
        }
        
        console.log('About to call audio.play()...');
        await audioRef.current.play();
        console.log('Audio.play() succeeded!');
        
        setIsPlaying(true);
        
        // Hide splash screen after first play with delayed fade
        if (!hasPlayedOnce) {
          setHasPlayedOnce(true);
          
          // Start fade out after 0.2s delay
          setTimeout(() => {
            setSplashScreenOpacity(0);
          }, 200);
          
          // Remove splash screen completely after fade animation (3s + 0.2s delay)
          setTimeout(() => {
            setShowSplashScreen(false);
          }, 3200);
        }
        
        // Trigger watermark animation when resuming play if UI is hidden
        if (!showUI) {
          triggerWatermarkAnimation();
        }
        
        // Check if audio is actually audible
        setTimeout(() => {
          if (audioRef.current) {
            console.log('Audio status check:', {
              currentTime: audioRef.current.currentTime,
              duration: audioRef.current.duration,
              volume: audioRef.current.volume,
              muted: audioRef.current.muted,
              paused: audioRef.current.paused,
              readyState: audioRef.current.readyState
            });
          }
        }, 1000);
        
        // Force a small delay to ensure the visualizer can setup properly in Safari
        if (navigator.userAgent.includes('Safari')) {
          setTimeout(() => {
            console.log('Safari: Triggering visualizer setup after play');
          }, 300);
        }
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
    }
  };

  const selectTrack = async (index: number, shouldPlay: boolean = isPlaying) => {
    console.log('selectTrack called:', { index, shouldPlay, currentTrack: audioTracks[index]?.title });
    
    // Pause current audio first
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    setCurrentTrackIndex(index);
    
    // Reset audio context connection for new track
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
        sourceRef.current = undefined;
        console.log('Disconnected previous audio source');
      } catch (e) {
        // Ignore disconnect errors
      }
    }
    
    // Update sequencer for new track
    const newTrack = audioTracks[index];
    const currentTime = audioRef.current?.currentTime || 0;
    sequencerEngine.updatePlayback(newTrack.id, currentTime, shouldPlay);
    setCurrentSequence(sequencerEngine.getCurrentSequence());
    
    if (shouldPlay && audioRef.current) {
      try {
        // Wait for audio element to load the new source
        const waitForLoad = () => {
          return new Promise<void>((resolve) => {
            if (audioRef.current && audioRef.current.readyState >= 2) {
              resolve();
            } else {
              const handleCanPlay = () => {
                audioRef.current?.removeEventListener('canplay', handleCanPlay);
                audioRef.current?.removeEventListener('loadeddata', handleCanPlay);
                resolve();
              };
              audioRef.current?.addEventListener('canplay', handleCanPlay);
              audioRef.current?.addEventListener('loadeddata', handleCanPlay);
              
              // Force load the new source
              if (audioRef.current) {
                audioRef.current.load();
              }
            }
          });
        };
        
        await waitForLoad();
        console.log('Audio loaded for track:', newTrack.title);
        
        // Setup audio context for new track
        await setupAudioContext().catch(error => {
          console.warn('Audio analysis setup failed for new track:', error);
        });
        
        // Play the new track
        await audioRef.current.play();
        setIsPlaying(true);
        console.log('Successfully playing track:', newTrack.title);
        
      } catch (error) {
        console.error('Error playing selected track:', error);
        setIsPlaying(false);
      }
    }
  };

  // Handle watermark animation
  const triggerWatermarkAnimation = () => {
    // Clear any existing watermark timeout
    if (watermarkTimeout) {
      clearTimeout(watermarkTimeout);
    }
    
    // Start fade-in animation
    setShowWatermark(true);
    setWatermarkOpacity(0);
    
    // Fade in over 1 second to full opacity
    const fadeInSteps = 20;
    const fadeInInterval = 50; // 50ms per step = 1 second total
    let currentStep = 0;
    
    const fadeInTimer = setInterval(() => {
      currentStep++;
      setWatermarkOpacity(currentStep / fadeInSteps);
      
      if (currentStep >= fadeInSteps) {
        clearInterval(fadeInTimer);
        // Ensure full opacity is set
        setWatermarkOpacity(1.0);
        
        // Stay at full opacity for 10 seconds, then fade out
        const timeout = setTimeout(() => {
          // Start fade-out animation from full opacity
          let fadeOutStep = fadeInSteps;
          const fadeOutTimer = setInterval(() => {
            fadeOutStep--;
            setWatermarkOpacity(fadeOutStep / fadeInSteps);
            
            if (fadeOutStep <= 0) {
              clearInterval(fadeOutTimer);
              setShowWatermark(false);
              setWatermarkOpacity(0);
            }
          }, fadeInInterval);
        }, 10000); // 10 seconds visible at full opacity
        
        setWatermarkTimeout(timeout);
      }
    }, fadeInInterval);
  };

  // Handle mouse detection for UI visibility
  const handleMouseDetection = () => {
    if (!showUI) {
      setShowUI(true);
    }
    
    // Clear existing timeout
    if (mouseTimeout) {
      clearTimeout(mouseTimeout);
    }
    
    // Set new timeout to hide UI after 8 seconds
    const timeout = setTimeout(() => {
      setShowUI(false);
      // Trigger watermark animation when UI hides
      if (isPlaying) {
        triggerWatermarkAnimation();
      }
    }, 8000);
    
    setMouseTimeout(timeout);
  };

  return (
    <div
      className="bg-[#000000] relative size-full"
      data-name="Music Experience"
      id="node-1_2"
    >
      <audio
        ref={audioRef}
        src={currentTrack.path}
        key={currentTrack.id}
        preload="auto"
        muted={false}
        crossOrigin={process.env.NODE_ENV === 'production' ? "anonymous" : undefined}
        onLoadedData={() => {
          console.log('Audio loaded:', currentTrack.title);
          // Ensure volume is set when audio loads
          if (audioRef.current) {
            audioRef.current.volume = 1.0;
            audioRef.current.muted = false;
          }
        }}
        onError={(e) => {
          console.error('Audio error:', e);
          console.error('Failed to load:', currentTrack.path);
          console.error('Error details:', e.currentTarget?.error);
          alert(`Audio failed to load: ${currentTrack.title}\nURL: ${currentTrack.path}\nError: ${e.currentTarget?.error?.message || 'Unknown error'}`);
        }}
        onCanPlay={() => {
          console.log('Audio can play:', currentTrack.title);
        }}
        onPlay={() => {
          console.log('Audio started playing:', currentTrack.title);
          setIsPlaying(true);
        }}
        onPause={() => {
          console.log('Audio paused:', currentTrack.title);
          setIsPlaying(false);
        }}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
            setDuration(audioRef.current.duration || 0);
          }
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            setDuration(audioRef.current.duration || 0);
          }
        }}
      />
      
      {/* Mouse Detection Overlay - Transparent overlay to capture mouse events */}
      {!showSidebar && !showPlaylist && (
        <div 
          className="absolute inset-0 w-full h-full z-20 pointer-events-auto"
          onMouseMove={handleMouseDetection}
          onMouseEnter={handleMouseDetection}
          style={{ backgroundColor: 'transparent' }}
        />
      )}
      
      {/* Sidebar Background Overlay - Click to close */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-50"
          onClick={() => setShowSidebar(false)}
          style={{ backdropFilter: 'blur(2px)' }}
        />
      )}
      
      <div className="relative size-full">
        <div className="box-border content-stretch flex flex-col items-start justify-between p-[96px] relative size-full">
          
          {/* Modern Playlist Dropdown */}
          <div
            className={`fixed bottom-28 left-6 w-80 bg-black bg-opacity-95 backdrop-blur-sm border border-gray-700 rounded-lg z-50 transition-all duration-300 ${
              showPlaylist && showUI ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none translate-y-4'
            }`}
            data-name="Modern Playlist"
          >
            <div className="p-4">
              {/* Playlist Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-['IBM_Plex_Mono:Bold',_sans-serif] text-sm">
                  Playlist
                </h3>
                <button
                  onClick={() => setShowPlaylist(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Close Playlist"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* Track List */}
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {audioTracks.map((track, index) => (
                  <button
                    key={track.id}
                    onClick={() => {
                      selectTrack(index, true);
                      setShowPlaylist(false);
                    }}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                      index === currentTrackIndex
                        ? 'bg-blue-600 bg-opacity-20 border border-blue-500 border-opacity-30'
                        : 'hover:bg-gray-800 hover:bg-opacity-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className={`font-['IBM_Plex_Mono:Bold',_sans-serif] text-sm truncate ${
                          index === currentTrackIndex ? 'text-blue-400' : 'text-white'
                        }`}>
                          {track.title}
                        </div>
                        <div className="text-gray-400 font-['IBM_Plex_Mono:Regular',_sans-serif] text-xs truncate">
                          Operator
                        </div>
                      </div>
                      {index === currentTrackIndex && (
                        <div className="ml-2 flex-shrink-0">
                          {isPlaying ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M3 2H6V14H3V2ZM10 2H13V14H10V2Z" fill="currentColor" className="text-blue-400"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M5 2L13 8L5 14V2Z" fill="currentColor" className="text-blue-400"/>
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Playlist Footer */}
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="text-gray-400 font-['IBM_Plex_Mono:Regular',_sans-serif] text-xs">
                  {audioTracks.length} tracks
                </div>
              </div>
            </div>
          </div>

          {/* Modern Player Control Bar */}
          <div
            className={`fixed bottom-0 left-0 right-0 bg-black bg-opacity-90 backdrop-blur-sm border-t border-gray-700 z-40 transition-all duration-500 ${
              showUI ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none translate-y-full'
            }`}
            data-name="Modern Player Control Bar"
          >
            <div className="px-6 py-4 max-w-screen-xl mx-auto">
              {/* First Row - Track Info and Controls aligned with progress bar */}
              <div className="flex items-center mb-4">
                {/* Left Section - Track Info (aligned with progress bar start) */}
                <div className="flex items-center space-x-4 min-w-0" style={{ width: '300px' }}>
                  <button
                    onClick={() => setShowPlaylist(!showPlaylist)}
                    className="min-w-0 text-left hover:bg-gray-800 hover:bg-opacity-50 rounded-lg p-2 transition-colors"
                    title="Open Playlist"
                  >
                    <div className="text-white font-['IBM_Plex_Mono:Bold',_sans-serif] text-sm truncate">
                      {currentTrack.title}
                    </div>
                    <div className="text-gray-400 font-['IBM_Plex_Mono:Regular',_sans-serif] text-xs truncate">
                      Operator
                    </div>
                  </button>
                </div>

                {/* Center Section - Media Controls */}
                <div className="flex items-center space-x-4 flex-1 justify-center">
                  <button
                    onClick={goToPrevious}
                    className="text-white hover:text-gray-300 transition-colors"
                    title="Previous Track"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M6 6H8V18H6V6ZM9.5 12L18 6V18L9.5 12Z" fill="currentColor"/>
                    </svg>
                  </button>

                  <button
                    onClick={togglePlay}
                    className="bg-white text-black hover:bg-gray-200 transition-colors rounded-full p-2"
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" fill="currentColor"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={goToNext}
                    className="text-white hover:text-gray-300 transition-colors"
                    title="Next Track"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M6 18L14.5 12L6 6V18ZM16 6V18H18V6H16Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>

                {/* Right Section - Controls (aligned with progress bar end) */}
                <div className="flex items-center space-x-4" style={{ width: '300px', justifyContent: 'flex-end' }}>
                  {/* Timeline Button - Only show in development */}
                  {process.env.NODE_ENV === 'development' && (
                    <button
                      onClick={() => setShowTimeline(!showTimeline)}
                      className={`text-white hover:text-gray-300 transition-colors ${
                        showTimeline ? 'text-blue-400' : ''
                      }`}
                      title="Toggle Timeline Sequencer"
                    >
                      <HugeiconsIcon icon={KeyframeAddIcon} />
                    </button>
                  )}

                  <button
                    onClick={() => setShowSidebar(!showSidebar)}
                    className={`text-white hover:text-gray-300 transition-colors ${
                      showSidebar ? 'text-blue-400' : ''
                    }`}
                    title="Show Track Info"
                  >
                    <HugeiconsIcon icon={TextFirstlineLeftIcon} />
                  </button>

                  <button
                    onClick={toggleFullscreen}
                    className="text-white hover:text-gray-300 transition-colors"
                    title="Toggle Fullscreen"
                  >
                    <HugeiconsIcon icon={SquareArrowExpand01Icon} />
                  </button>
                </div>
              </div>

              {/* Second Row - Progress Bar */}
              <div className="flex items-center space-x-2">
                <span className="text-gray-400 text-xs font-['IBM_Plex_Mono:Regular',_sans-serif] min-w-[40px]">
                  {formatTime(currentTime)}
                </span>
                <div
                  className="flex-1 h-1 bg-gray-600 rounded-full cursor-pointer"
                  onClick={handleSeekBar}
                >
                  <div
                    className="h-full bg-white rounded-full transition-all duration-100"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-gray-400 text-xs font-['IBM_Plex_Mono:Regular',_sans-serif] min-w-[40px]">
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Full-screen Visualizer Wrapper */}
          <div
            className="absolute inset-0 w-full h-full z-0"
            data-name="Visualizer Wrapper"
            id="node-1_38"
          >
            <ShadertoyRenderer 
              audioData={audioData} 
              isPlaying={isPlaying}
              shaderCode={shaderCode}
              customParameters={customParameters}
              audioMappings={audioMappings}
              audioContextRef={audioContextRef}
              audioChannels={audioChannels}
            />
          </div>
          
          {/* Initial Splash Background - Shows only on first load */}
          {showSplashScreen && (
            <div
              className="absolute inset-0 w-full h-full z-10"
              style={{
                backgroundImage: process.env.NODE_ENV === 'production' 
                  ? 'url(https://m4cidiwracjjmpal.public.blob.vercel-storage.com/operator-initial-bg.png)'
                  : 'url(/operator-initial-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: splashScreenOpacity,
                transition: 'opacity 3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              data-name="Initial Splash Background"
            />
          )}
          
          {/* Pause Background - Shows after first play when paused */}
          {!showSplashScreen && (
            <div
              className={`absolute inset-0 w-full h-full z-10 transition-opacity duration-500 ${
                isPlaying ? 'opacity-0' : 'opacity-100'
              }`}
              style={{
                backgroundImage: process.env.NODE_ENV === 'production' 
                  ? 'url(https://m4cidiwracjjmpal.public.blob.vercel-storage.com/centered-bg.png)'
                  : 'url(/centered-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
              data-name="Pause Background"
            />
          )}
          
          {/* Watermark - Positioned at bottom like control bar */}
          {showWatermark && (
            <div
              className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-center pointer-events-none pb-8"
              style={{
                opacity: watermarkOpacity,
                transition: 'opacity 0.1s ease-in-out'
              }}
              data-name="Watermark"
            >
              <img
                src={process.env.NODE_ENV === 'production' 
                  ? "https://m4cidiwracjjmpal.public.blob.vercel-storage.com/watermark.svg"
                  : "/assets/watermark.svg"}
                alt="Watermark"
                className="max-w-4xl w-full h-auto"
                style={{
                  filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))'
                }}
              />
            </div>
          )}
          
        </div>
        
        {/* Translucent Sidebar - 20% of right side */}
        {showSidebar && (
          <div
            className="fixed top-0 right-0 w-1/5 bg-black bg-opacity-70 backdrop-blur-sm border-l border-gray-700 transition-all duration-500 pointer-events-auto"
            style={{ 
              height: '100vh',
              paddingBottom: showUI ? '120px' : '0px', // Offset for control bar height
              zIndex: 9999 // Ensure it's on top
            }}
            data-name="Track Info Sidebar"
            onClick={(e) => e.stopPropagation()} // Prevent event bubbling
          >
            <div className="h-full flex flex-col">
              {/* Header - Fixed at top */}
              <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
                <h2 className="text-white font-['IBM_Plex_Mono:Bold',_sans-serif] text-xl uppercase text-left">
                  {currentTrack.title}
                </h2>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSidebar(false);
                  }}
                  className="text-white hover:text-gray-300 transition-colors p-2 hover:bg-gray-700 rounded"
                  title="Close Sidebar"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>

              {/* Track Information - Scrollable content */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-300 font-['IBM_Plex_Mono:Regular',_sans-serif] text-sm leading-loose text-justify uppercase whitespace-pre-line">
                      {getTrackBlurb(currentTrack.id)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
      </div>
      

      {/* Shader Editor - Only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`transition-opacity duration-500 z-50 ${
          showUI ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}>
          <ShaderEditor 
          onShaderChange={(code, channels) => {
            setShaderCode(code);
            if (channels) {
              setAudioChannels(channels);
              console.log('Audio channel mapping updated:', channels);
            }
          }}
          onClose={() => {}}
          initialCode={shaderCode}
          onOpenParameters={() => setShowParameterPanel(true)}
        />
        </div>
      )}
      
      {/* Shader Parameter Panel - Only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`transition-opacity duration-500 z-50 ${
          showUI ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}>
          <ShaderParameterPanel
            shaderCode={shaderCode}
            onParametersChange={setCustomParameters}
            onAudioMappingsChange={setAudioMappings}
            isVisible={showParameterPanel}
            onClose={() => setShowParameterPanel(false)}
          />
        </div>
      )}

      {/* Timeline Sequencer - Only show in development */}
      {process.env.NODE_ENV === 'development' && showTimeline && (
        <div 
          className="fixed bottom-0 left-0 right-0 bg-gray-900 bg-opacity-95 border-t border-gray-700 transition-all duration-500"
          style={{ zIndex: 9999 }}
        >
          <TimelineSequencerUI
            sequence={currentSequence}
            currentTime={audioRef.current?.currentTime || 0}
            duration={getAudioDuration()}
            isPlaying={isPlaying}
            onSeek={handleSeek}
            onAddEvent={handleAddEvent}
            onEditEvent={handleEditEvent}
            onDeleteEvent={handleDeleteEvent}
            onExport={handleExportTimeline}
            onImport={handleImportTimeline}
            onClose={() => setShowTimeline(false)}
            className="p-4"
          />
        </div>
      )}

      {/* Timeline Event Editor - Only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <TimelineEventEditor
          event={editingEvent}
          timestamp={newEventTimestamp}
          isVisible={showEventEditor}
          onSave={handleSaveEvent}
          onCancel={() => {
            setShowEventEditor(false);
            setEditingEvent(undefined);
          }}
        />
      )}
      
    </div>
  );
}